import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import EntityDialog from '../../EntityDialog';
import { USER_ROLE_OPTIONS } from '../../constants';
import {
  editUser,
  getUserAccessBrands,
  getUserAccessFacilities,
  getUserAccessMarkets,
  getUsers,
  removeUser,
} from '../../hooks/useUsers';
import {
  normalizeScopeIds,
  toUserAccessScopePayload,
  validateUserAccessScope,
} from '../../utils/permissions';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import ConfirmDialog from '../../ConfirmDialog';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { getErrorMessage } from '../../utils/requestErrors';
import UserAccessScopeFields from '../../UserAccessScopeFields';
import { useOrganizationById } from '../../hooks/useOrganizations';

const userAccessApi = {
  getUserAccessMarkets,
  getUserAccessFacilities,
  getUserAccessBrands,
};

export default function EditUserContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const {
    currentUser,
    org,
  } = useLegacyAppSession();
  const userId = String(params.userId || '');
  const [user, setUser] = useState(location.state?.user || null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(!location.state?.user);
  const organizationId = user?.organizationId ?? org?.id ?? null;
  const organizationRequest = useOrganizationById(organizationId, Boolean(organizationId));

  useEffect(() => {
    if (!userId || !org?.id || user) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getUsers({ organizationId: org.id, page: 0, pageSize: 500 })
      .then((users) => {
        if (cancelled) return;
        const match = (users || []).find((item) => String(item?.id) === userId) || null;
        setUser(match);
      })
      .catch(() => {
        if (cancelled) return;
        showToastError('User could not be loaded.');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [org?.id, user, userId]);

  if (!userId) {
    return <Navigate to="/users" replace />;
  }

  if (isLoading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/users" replace />;
  }

  const organizationName = organizationRequest.data?.name || org?.name || 'this organization';

  if (showRemoveConfirm) {
    return (
      <div style={{ padding: '16px 16px 0' }}>
        <ConfirmDialog
          title="Remove from organization"
          message={`Remove ${user?.email || user?.name || 'this user'} from ${organizationName}?`}
          confirmLabel="Remove"
          cancelLabel="Cancel"
          testId="remove-user-from-org"
          onCancel={() => setShowRemoveConfirm(false)}
          onConfirm={async () => {
            if (!organizationId) return;
            try {
              await removeUser({
                userId: user?.id,
                organizationId,
              });
              showToastSuccess('User successfully removed from the organization.');
              navigate('/users', { replace: true });
            } catch (error) {
              console.error(error);
            }
          }}
        />
      </div>
    );
  }

  return (
    <EntityDialog
      title="Edit User"
      contextLabel="Users"
      testId="edit-user"
      fields={[
        { name: 'name', label: 'Name', required: true, readOnly: true },
        { name: 'email', label: 'Email', required: true, readOnly: true },
        {
          name: 'roleName',
          label: 'Role',
          type: 'select',
          options: USER_ROLE_OPTIONS,
          required: true,
        },
        {
          name: 'accessScope',
          type: 'org-selector',
          excludeFromPreview: true,
          render: ({ form, setValue }) => (
            <UserAccessScopeFields
              api={userAccessApi}
              organizationId={organizationId}
              form={form}
              setValue={setValue}
            />
          ),
        },
        ...(currentUser?.roleActions?.includes('AdminUserManagement')
          ? [{
              name: 'removeFromOrganization',
              label: 'Organization',
              render: () => (
                <div className="stack" style={{ gap: 8 }}>
                  <div className="muted" style={{ fontSize: 12 }}>
                    Remove this user from the current organization.
                  </div>
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => setShowRemoveConfirm(true)}
                    disabled={!organizationId}
                    data-testid="edit-user-remove-organization"
                  >
                    Remove from organization
                  </button>
                </div>
              ),
            }]
          : []),
      ]}
      initialValues={{
        ...(user || {}),
        email: user?.email || '',
        facilities: Array.isArray(user?.facilities) ? user.facilities : [user?.facilities].filter(Boolean),
        roleName: user?.roleName || 'Viewer',
        removeFromOrganization: organizationName,
        allMarkets: user?.allMarkets === true,
        marketIds: normalizeScopeIds(user?.marketIds),
        defaultMarketId: normalizeScopeIds(user?.defaultMarketId)[0] ?? null,
        allFacilities: user?.allFacilities === true,
        facilityIds: normalizeScopeIds(user?.facilityIds),
        allBrands: user?.allBrands === true,
        brandIds: normalizeScopeIds(user?.brandIds),
      }}
      onClose={() => navigate('/users', { replace: true })}
      onSave={async (payload) => {
        try {
          if (!organizationId) return;
          const scopeError = validateUserAccessScope(payload);
          if (scopeError) {
            return showToastError(scopeError);
          }

          await editUser({
            userId: user?.id,
            organizationId,
            roleName: payload.roleName,
            ...toUserAccessScopePayload(payload),
          });

          showToastSuccess('User was updated successfully.');
          navigate('/users', { replace: true });
        } catch (error) {
          console.error(error);
          showToastError(getErrorMessage(error, 'An error occurred updating the user.'));
        }
      }}
    />
  );
}
